import { toHex } from '../bytes/encoding';
import type { LogDescriptor } from '../log/client';
import { logSlug, operatorSlug } from './slug';

/** Readable address of a log: `#/logs/<op>/<log>`. */
export interface LogPath {
  op: string;
  log: string;
}

/** Merged view over list logs and custom logs, with fast lookups. */
export class LogRegistry {
  private byIdMap = new Map<string, LogDescriptor>();
  private paths?: { byPath: Map<string, LogDescriptor>; ofId: Map<string, LogPath> };

  constructor(descriptors: Iterable<LogDescriptor> = []) {
    for (const d of descriptors) this.byIdMap.set(d.id, d);
  }

  all(): LogDescriptor[] {
    return [...this.byIdMap.values()];
  }

  byId(id: string): LogDescriptor | undefined {
    return this.byIdMap.get(id);
  }

  /** Exact id, or a unique prefix of at least 6 characters (for hand-typed / shortened links). */
  byIdPrefix(prefix: string): LogDescriptor | undefined {
    const exact = this.byIdMap.get(prefix);
    if (exact || prefix.length < 6) return exact;
    const p = prefix.toLowerCase();
    let found: LogDescriptor | undefined;
    for (const d of this.byIdMap.values()) {
      if (!d.id.startsWith(p)) continue;
      if (found) return undefined; // ambiguous
      found = d;
    }
    return found;
  }

  /** Looks up by 32-byte log ID (e.g. from an SCT). */
  byLogId(logId: Uint8Array): LogDescriptor | undefined {
    return this.byIdMap.get(toHex(logId));
  }

  byUrl(url: string): LogDescriptor | undefined {
    const u = url.endsWith('/') ? url : url + '/';
    return this.all().find((d) => d.url === u || d.monitoringUrl === u || d.submissionUrl === u);
  }

  /** Descriptors grouped by operator, in insertion order. */
  byOperator(): Map<string, LogDescriptor[]> {
    const m = new Map<string, LogDescriptor[]>();
    for (const d of this.byIdMap.values()) {
      const arr = m.get(d.operator);
      if (arr) arr.push(d);
      else m.set(d.operator, [d]);
    }
    return m;
  }

  /** Operator name for an operator slug (`google` → "Google"). */
  operatorBySlug(op: string): string | undefined {
    for (const name of this.byOperator().keys()) if (operatorSlug(name) === op) return name;
    return undefined;
  }

  /** `#/logs/<op>/<log>` segments of a log. Slugs that collide within an operator get an id suffix. */
  pathOf(log: LogDescriptor): LogPath {
    return this.ensurePaths().ofId.get(log.id) ?? { op: operatorSlug(log.operator), log: log.id };
  }

  byPath(op: string, log: string): LogDescriptor | undefined {
    return this.ensurePaths().byPath.get(`${op}/${log}`) ?? this.byPathId(op, log);
  }

  /** Accepts the log id (or a unique prefix) in place of the slug, so links survive renames. */
  private byPathId(op: string, log: string): LogDescriptor | undefined {
    const d = this.byIdPrefix(log);
    return d && operatorSlug(d.operator) === op ? d : undefined;
  }

  private ensurePaths(): { byPath: Map<string, LogDescriptor>; ofId: Map<string, LogPath> } {
    if (this.paths) return this.paths;
    const byPath = new Map<string, LogDescriptor>();
    const ofId = new Map<string, LogPath>();
    const counts = new Map<string, number>();
    const keyed = this.all().map((d) => ({ d, op: operatorSlug(d.operator), slug: logSlug(d) }));
    for (const k of keyed) counts.set(`${k.op}/${k.slug}`, (counts.get(`${k.op}/${k.slug}`) ?? 0) + 1);
    for (const { d, op, slug } of keyed) {
      const log = counts.get(`${op}/${slug}`)! > 1 ? `${slug}-${d.id.slice(0, 8)}` : slug;
      byPath.set(`${op}/${log}`, d);
      ofId.set(d.id, { op, log });
    }
    this.paths = { byPath, ofId };
    return this.paths;
  }

  with(extra: Iterable<LogDescriptor>): LogRegistry {
    return new LogRegistry([...this.byIdMap.values(), ...extra]);
  }
}
